import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { PolicyContext, PolicyData } from '../../context/policy';
import LargeCenterSpinner from '../LargeCenterSpinner';
import { NonQuoteApplicationError } from '../NonQuoteApplicationError';
import ProtectedRoute from '../ProtectedRoute';
import { errorToast } from '../Toast';
import PolicyRoutes from './Routes/PolicyRoutes';
import QuoteRoutes from './Routes/QuoteRoutes';

interface Props {
	policyData: PolicyData;
	configData: ConfigData;
	authData: AuthData;
}

/** Routes for '/policy/:policyId' */
const PolicyQuoteRoutes = (props: Props) => {
	const params = useParams();
	const { policyData } = props;

	const { quotes, selectedQuote, policy, graceLapse } = policyData;

	const [loading, setLoading] = useState<boolean>(true);
	const source = axios.CancelToken.source();

	const loadData = async () => {
		try {
			setLoading(true);
			await policyData.loadData(params.policyId, {
				cancelToken: source.token,
			});
			setLoading(false);
		} catch (error) {
			if (axios.isCancel(error)) {
				return;
			} else {
				errorToast('Failed to load necessary data');
			}
		}
	};

	useEffect(() => {
		loadData();

		return () => {
			source.cancel();
		};
	}, [params.policyId]);

	// if policy is issued, go to policy routes
	if (loading || !graceLapse) {
		return <LargeCenterSpinner />;
	}

	if (!policy && !quotes) {
		return <LargeCenterSpinner />;
	}

	// if there are no quotes AND the policy is not issued, route to error page
	if (!quotes?.length && !policy.issuedTimestamp) {
		return <NonQuoteApplicationError />;
	}

	// missing selectedQuote means quote feature is not enabled
	if (!selectedQuote || (policy && policy.issuedTimestamp)) {
		return <PolicyRoutes />;
	}

	const { locator } = selectedQuote;

	return (
		<PolicyContext.Provider value={policyData}>
			<Routes>
				<Route
					path='quotes/:quoteId/*'
					element={
						<ProtectedRoute permission='quote.fetch'>
							<QuoteRoutes />
						</ProtectedRoute>
					}
				/>
				<Route
					path='*'
					element={<Navigate replace to={`quotes/${locator}`} />}
				/>
			</Routes>
		</PolicyContext.Provider>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData' | 'policyData'>) => (
		<PolicyContext.Consumer>
			{(policyData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									policyData={policyData}
									configData={configData}
									authData={authData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</ConfigContext.Consumer>
			)}
		</PolicyContext.Consumer>
	);
}

export default withContext(PolicyQuoteRoutes);
